// libraries
import { makeAutoObservable, runInAction } from "mobx";
// utils
import { makeRandomDBName } from "utils/utilFunctions";
// constants
import serviceCreationData from "data/service_creation.json";
// types
import type { Maybe, Type } from "graphql/generated";
import { ServiceType } from "types";

export type { Configuration } from "utils/productCatalog";

export type ConfiguredService = {
  name: string;
  milliCPU: number;
  memoryGB: number;
  storageGB: number;
  serviceType: Type;
  vpc?: string;
  regionCode: string;
  replicaCount: number;
  vpcId?: Maybe<string>;
  enableConnectionPooler?: boolean;
  readReplicaSetCount?: number;
};

const defaultConfiguredService: ConfiguredService = {
  // this is so that when switching back
  // & forth between default config & adv,
  // the service name field stays (otherwise it'd be blank)
  name: makeRandomDBName(),
  serviceType: ServiceType.Timescaledb,
  ...serviceCreationData.forms.defaultConfiguration,
};

export class ProductStore {
  /** A configured service in e.g. Create a Service forms,
   *  used to estimate pricing.
   */
  configuredService = defaultConfiguredService;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   *  Creates a service config that we can
   *  use to make pricing calculations, e.g.
   *  in Create a Service forms.
   *
   *  This is intended to be triggered every time
   *  one of the relevant service properties are
   *  updated, e.g. they're changed within the Create
   *  a Service form, so that "configuredService"
   *  stays up to date.
   */
  savedServices = (service: ConfiguredService) => {
    const {
      name,
      milliCPU,
      memoryGB,
      storageGB,
      serviceType,
      vpc,
      regionCode,
      replicaCount,
      ...rest
    } = service;

    let newConfiguredService = {
      ...this.configuredService,
      serviceType,
      ...rest,
    };
    if (name || typeof name === "string") {
      newConfiguredService = {
        ...newConfiguredService,
        name,
      };
    }
    if (milliCPU || memoryGB) {
      newConfiguredService = {
        ...newConfiguredService,
        milliCPU,
        memoryGB,
      };
    }
    if (regionCode) {
      newConfiguredService = {
        ...newConfiguredService,
        regionCode,
      };
    }
    if (typeof replicaCount === "number") {
      newConfiguredService = {
        ...newConfiguredService,
        replicaCount,
      };
    }
    newConfiguredService = {
      ...newConfiguredService,
      storageGB: storageGB ?? newConfiguredService.storageGB,
      vpc,
    };
    runInAction(() => {
      this.configuredService = newConfiguredService;
    });
  };

  resetServices = () => {
    runInAction(() => {
      this.configuredService = defaultConfiguredService;
    });
  };
}

export const productStore = new ProductStore();
